import React, { useState } from "react";
import {
  List,
  Avatar,
  Tag,
  Empty,
  Divider,
  Card,
  Button,
  Space,
  Tooltip,
  message,
} from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  TagOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import Draggable from "react-draggable";
import dayjs, { Dayjs } from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import "./AgendaView.css";
import { Event } from "./activityCalendar";
import api from "../../../services/api";

// Day.js plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface AgendaViewProps {
  currentDate: Dayjs;
  events: Event[];
  onEventClick: (event: Event) => void;
  onEventSaved?: () => void;
  selectedCompany: { id: string; type: string } | null;
}

const AgendaView: React.FC<AgendaViewProps> = ({
  currentDate,
  events,
  onEventClick,
  onEventSaved,
}) => {
  const signature = localStorage.getItem("signature");
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isEventDetailVisible, setIsEventDetailVisible] = useState(false);
  const [detailPosition, setDetailPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Handle event click in agenda view
  const handleEventClick = (event: Event, clickEvent?: React.MouseEvent) => {
    setSelectedEvent(event);

    // Calculate position for the detail card
    const container = document.querySelector(".agenda-view") as HTMLElement;
    const containerRect = container.getBoundingClientRect();

    let x = 50; // Default position
    let y = 50;

    if (clickEvent) {
      x = clickEvent.clientX - containerRect.left;
      y = clickEvent.clientY - containerRect.top;
    }

    const cardWidth = 400;
    const cardHeight = 500;

    // Ensure card stays within bounds
    if (x + cardWidth > containerRect.width) {
      x = containerRect.width - cardWidth - 20;
    }
    if (y + cardHeight > containerRect.height) {
      y = containerRect.height - cardHeight - 20;
    }
    if (x < 20) x = 20;
    if (y < 20) y = 20;

    setDetailPosition({ x, y });
    setIsEventDetailVisible(true);
  };

  const handleCloseDetail = () => {
    setIsEventDetailVisible(false);
    setSelectedEvent(null);
  };

  const handleEditEvent = () => {
    if (selectedEvent) {
      onEventClick(selectedEvent);
      setIsEventDetailVisible(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent?.id) return;

    try {
      await api.post("activityCalendarService/deleteEvent", {
        signature,
        id: selectedEvent.id,
      });
      message.success("Event deleted successfully");
      setIsEventDetailVisible(false);
      setSelectedEvent(null);
      onEventSaved?.();
    } catch (err) {
      message.error("Failed to delete event");
    }
  };

  const formatEventTime = (event: Event): string => {
    if (event.allDay) return "All day";

    const isMultiDay = !event.start.isSame(event.end, "day");
    if (isMultiDay) {
      return `${event.start.format("h:mm A")} - ${event.end.format(
        "h:mm A"
      )} (${event.end.format("MMM D")})`;
    }

    return `${event.start.format("h:mm A")} - ${event.end.format("h:mm A")}`;
  };

  const getEventAvatar = (event: Event): string => {
    return event.title.charAt(0).toUpperCase();
  };

  const getEventAvatarColor = (event: Event): string => {
    return event.color;
  };

  // Group events by date
  const groupedEvents: { [key: string]: Event[] } = {};

  events.forEach((event) => {
    const dateKey = event.start.format("YYYY-MM-DD");
    if (!groupedEvents[dateKey]) {
      groupedEvents[dateKey] = [];
    }
    groupedEvents[dateKey].push(event);
  });

  // Sort dates chronologically
  const sortedDates = Object.keys(groupedEvents).sort();

  const getDateLabel = (dateString: string): string => {
    const date = dayjs(dateString);
    const today = dayjs().startOf("day");
    const tomorrow = today.add(1, "day");

    if (date.isSame(today, "day")) return "Today";
    if (date.isSame(tomorrow, "day")) return "Tomorrow";
    if (date.isBefore(today)) return date.format("MMMM D, YYYY");
    return date.format("dddd, MMMM D");
  };

  if (events.length === 0) {
    return (
      <div className="agenda-view">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No upcoming events"
        />
      </div>
    );
  }

  return (
    <div
      className="agenda-view"
      style={{ position: "relative", minHeight: "600px" }}
    >
      {sortedDates.map((dateKey, index) => {
        const dateEvents = groupedEvents[dateKey];
        const date = dayjs(dateKey);
        const isPast = date.isBefore(dayjs().startOf("day"));

        return (
          <div key={dateKey} className="agenda-day-group">
            <Divider orientation="left" className="date-divider">
              <div className="date-header">
                <span className="date-label">{getDateLabel(dateKey)}</span>
                <Tag color={isPast ? "default" : "blue"} className="date-tag">
                  {date.format("MMM D")}
                </Tag>
              </div>
            </Divider>

            <List
              className="event-list"
              dataSource={dateEvents.sort((a, b) => a.start.diff(b.start))}
              renderItem={(event, eventIndex) => (
                <Tooltip
                  key={event.id}
                  title="Click to view details"
                  placement="left"
                >
                  <List.Item
                    className={`event-item ${isPast ? "past-event" : ""}`}
                    onClick={(e) => handleEventClick(event, e)}
                    actions={[
                      <span key="time" className="event-time">
                        {formatEventTime(event)}
                      </span>,
                    ]}
                    style={{ cursor: "pointer" }}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          style={{
                            backgroundColor: getEventAvatarColor(event),
                          }}
                          className="event-avatar"
                        >
                          {getEventAvatar(event)}
                        </Avatar>
                      }
                      title={
                        <div className="event-title-section">
                          <span className="event-title">{event.title}</span>
                          {event.location && (
                            <span className="event-location">
                              {" "}
                              • {event.location}
                            </span>
                          )}
                          {event.allDay && (
                            <Tag color="green" className="text-xs px-2 py-0.5">
                              All Day
                            </Tag>
                          )}
                          {event.is_public && (
                            <Tag color="blue" className="text-xs px-2 py-0.5">
                              🌐 Public
                            </Tag>
                          )}
                        </div>
                      }
                      description={
                        event.description ? (
                          <div className="event-description">
                            {event.description}
                          </div>
                        ) : null
                      }
                    />
                  </List.Item>
                </Tooltip>
              )}
            />
          </div>
        );
      })}

      {/* Event Detail Card */}
      {isEventDetailVisible && selectedEvent && detailPosition && (
        <Draggable
          handle=".card-header"
          bounds=".agenda-view"
          defaultPosition={detailPosition}
        >
          <Card
            className="event-detail-card"
            style={{
              width: 400,
              zIndex: 1001,
              position: "absolute",
              top: 0,
              left: 0,
            }}
            styles={{
              body: { padding: 0 },
            }}
          >
            {/* Custom Header */}
            <div
              className="card-header detail-header"
              style={{
                background: `linear-gradient(135deg, ${selectedEvent.color}20 0%, ${selectedEvent.color}40 100%)`,
                borderLeft: `4px solid ${selectedEvent.color}`,
              }}
            >
              <div className="card-header-content">
                <div className="card-title-section">
                  <div
                    className="card-icon"
                    style={{ backgroundColor: selectedEvent.color }}
                  >
                    <CalendarOutlined
                      style={{ color: "white", fontSize: "16px" }}
                    />
                  </div>
                  <div>
                    <div className="card-title">{selectedEvent.title}</div>
                    <div className="card-subtitle">
                      {formatEventTime(selectedEvent)}
                    </div>
                  </div>
                </div>
                <Button
                  type="text"
                  onClick={handleCloseDetail}
                  className="close-btn"
                  icon={<CloseOutlined />}
                />
              </div>
            </div>

            {/* Detail Content */}
            <div className="card-body">
              <div className="event-details">
                {/* Event Information */}
                <div className="detail-section">
                  <div className="detail-item">
                    <div className="detail-label">
                      <ClockCircleOutlined className="detail-icon" />
                      Time
                    </div>
                    <div className="detail-value">
                      {selectedEvent.allDay ? (
                        "All Day"
                      ) : (
                        <>
                          {selectedEvent.start.format(
                            "ddd, MMM D, YYYY h:mm A"
                          )}
                          <br />
                          to{" "}
                          {selectedEvent.end.format("ddd, MMM D, YYYY h:mm A")}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-label">
                      <TagOutlined className="detail-icon" />
                      Category
                    </div>
                    <div className="detail-value">
                      <Tag
                        color={selectedEvent.color}
                        style={{ border: `1px solid ${selectedEvent.color}` }}
                      >
                        {selectedEvent.category}
                      </Tag>
                    </div>
                  </div>

                  {selectedEvent.location && (
                    <div className="detail-item">
                      <div className="detail-label">
                        <EnvironmentOutlined className="detail-icon" />
                        Location
                      </div>
                      <div className="detail-value">
                        {selectedEvent.location}
                      </div>
                    </div>
                  )}

                  <div className="detail-item">
                    <div className="detail-label">
                      <FileTextOutlined className="detail-icon" />
                      Visibility
                    </div>
                    <div className="detail-value">
                      <Tag color={selectedEvent.is_public ? "blue" : "default"}>
                        {selectedEvent.is_public
                          ? "Public Event"
                          : "Private Event"}
                      </Tag>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {selectedEvent.description && (
                  <>
                    <Divider style={{ margin: "16px 0" }} />
                    <div className="detail-section">
                      <div className="detail-item">
                        <div className="detail-label">
                          <FileTextOutlined className="detail-icon" />
                          Description
                        </div>
                        <div className="detail-value description-text">
                          {selectedEvent.description}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Action Buttons */}
                <Divider style={{ margin: "16px 0" }} />
                <div className="detail-actions">
                  <Space
                    style={{ width: "100%", justifyContent: "space-between" }}
                  >
                    <Button
                      type="primary"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteEvent}
                      size="middle"
                      disabled={selectedEvent?.is_generated}
                    >
                      Delete
                    </Button>
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={handleEditEvent}
                      size="middle"
                      disabled={selectedEvent?.is_generated}
                    >
                      Edit Event
                    </Button>
                  </Space>
                </div>
              </div>
            </div>
          </Card>
        </Draggable>
      )}
    </div>
  );
};

export default AgendaView;
